import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ImageOff, Package, Skull, UserRound, X } from 'lucide-react';
import { markImageFailed } from '../../utils/entityImages';
import { SpriteBox } from './SpriteBox';
import { CroppedPhoto } from './CroppedPhoto';
import type { ImageEntry } from '../../data/guide/entityImages';
import type { SpriteRef } from '../../utils/itemSprites';
type Size = 'thumb' | 'card' | 'hero';
const box: Record<Size, string> = {
  thumb: 'h-10 w-10 rounded-lg',
  card: 'h-14 w-14 rounded-xl',
  hero: 'h-24 w-24 md:h-36 md:w-36 rounded-2xl'
};
const iconCls: Record<Size, string> = {
  thumb: 'h-4 w-4',
  card: 'h-5 w-5',
  hero: 'h-8 w-8 md:h-10 md:w-10'
};
const GAP = 12;
const MARGIN = 8;
const CLOSE_DELAY = 120;
const canHover = () => typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

/**
 * Imagen de una clase, NPC o monstruo.
 * - Miniatura: rellena todo el cuadro (object-cover, encuadre hacia arriba para no cortar la cabeza), sin franjas vacías.
 * - Nunca activa la tarjeta o enlace que la contenga.
 * - En computadora: vista previa ampliada junto a la miniatura al pasar el cursor o al enfocarla con el teclado.
 *   Se mantiene mientras el cursor esté sobre la miniatura o la vista previa; se cierra al salir de ambas o con Escape.
 * - En celular: tocar la foto abre una vista previa centrada con la imagen completa; se cierra tocando fuera o con la X.
 * - Sin imagen o si falla: marcador neutro.
 */
export function EntityImage({
  entry,
  alt,
  name,
  size = 'thumb',
  lazy = true,
  kind = 'monstruo',
  focusable = true,
  preview = true,
  sprite
}: {
  /** Ítem: una celda de una lámina de ítems (sustituye a entry). */
  sprite?: SpriteRef;
  /** false: sin vista previa; el clic pasa al elemento que la contiene (p. ej. seleccionar la clase). */
  preview?: boolean;
  entry?: ImageEntry;
  alt: string;
  /** Nombre que se muestra en la vista previa (por defecto, el de la entrada). */
  name?: string;
  size?: Size;
  lazy?: boolean;
  kind?: 'monstruo' | 'persona' | 'item';
  /** false en contextos donde el foco no debe moverse (p. ej. resultados del buscador). */
  focusable?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  const [open, setOpen] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [pos, setPos] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const thumbRef = useRef<HTMLSpanElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number>();
  const reduceMotion = useReducedMotion();
  const src = sprite?.url ?? entry?.image?.src;
  const label = name ?? entry?.label ?? alt;
  const frame = `${box[size]} shrink-0 overflow-hidden border border-white/10 bg-[#0A1628]`;
  const fail = useCallback(() => {
    setBroken(true);
    if (src) markImageFailed(src);
  }, [src]);

  /** Ítems: celda de la lámina. Resto: la imagen; la miniatura rellena el cuadro, la vista previa la muestra completa. */
  const visual = (mode: 'thumb' | 'preview' | 'sheet') => {
    if (sprite) {
      const cls = mode === 'thumb' ? 'h-full w-full' : mode === 'preview' ? 'h-56 w-56 max-w-full' : 'aspect-square w-full max-w-[20rem]';
      return <SpriteBox cell={sprite} url={sprite.url} bounds={sprite.bounds} lazy={mode === 'thumb' && lazy} className={cls} onError={fail} data-id="element-1177" data-mp="1gy7o1i8ikef8" />;
    }
    const crop = entry?.image?.crop;
    if (crop && src) {
      if (mode === 'thumb') return <CroppedPhoto src={src} crop={crop} alt={alt} fit="cover" lazy={lazy} className="h-full w-full" onError={fail} data-id="element-1178" data-mp="1372kw65xt405" />;
      if (mode === 'preview') return <CroppedPhoto src={src} crop={crop} alt="" fit="full" lazy={false} className="w-[16.5rem] max-w-full" onLoad={place} data-id="element-1179" data-mp="y9ap31268xqo" />;
      return <CroppedPhoto src={src} crop={crop} alt={alt} fit="full" lazy={false} className="w-full" data-id="element-1180" data-mp="tapqf62kim4c" />;
    }
    if (mode === 'thumb') return <img src={src} alt={alt} loading={lazy ? 'lazy' : 'eager'} decoding="async" draggable={false} className={kind === 'item' ? 'h-[88%] w-[88%] object-contain' : 'h-full w-full object-cover object-[50%_30%]'} onError={fail} data-id="element-1181" data-mp="1dxfmuh23a62d" />;
    if (mode === 'preview') return <img src={src} alt="" draggable={false} onLoad={place} className="block h-auto max-h-[min(20rem,calc(100vh-80px))] w-auto max-w-full object-contain" data-id="element-1182" data-mp="153t3jqf2iva2" />;
    return <img src={src} alt={alt} draggable={false} className="block h-auto max-h-[70vh] w-full object-contain" data-id="element-1183" data-mp="v6oglodeevs7" />;
  };
  const cancelClose = () => window.clearTimeout(closeTimer.current);
  const show = useCallback(() => {
    cancelClose();
    setOpen(true);
  }, []);
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), CLOSE_DELAY);
  };

  /** Coloca la vista previa a la derecha (o a la izquierda si no cabe), dentro de la pantalla. */
  const place = useCallback(() => {
    const t = thumbRef.current;
    const p = previewRef.current;
    if (!t || !p) return;
    const r = t.getBoundingClientRect();
    const pw = p.offsetWidth;
    const ph = p.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = r.right + GAP;
    if (left + pw > vw - MARGIN) left = r.left - GAP - pw;
    left = Math.max(MARGIN, Math.min(left, vw - pw - MARGIN));
    const top = Math.max(MARGIN, Math.min(r.top + r.height / 2 - ph / 2, vh - ph - MARGIN));
    setPos({
      left,
      top
    });
  }, []);
  useLayoutEffect(() => {
    if (open) place();else setPos(null);
  }, [open, place]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onScroll = () => setOpen(false);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);
  useEffect(() => () => cancelClose(), []);

  /** Vista previa en celular: Escape cierra y se bloquea el desplazamiento del fondo mientras está abierta. */
  useEffect(() => {
    if (!sheet) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSheet(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [sheet]);

  // Falla de carga: marcador propio ("la imagen no cargó"), distinto del de "sin imagen".
  if (src && broken) {
    return <span className={`${frame} flex items-center justify-center text-amber-300/70`} role="img" aria-label={`${label}: la imagen no cargó`} title="La imagen no cargó" data-id="element-1184" data-mp="202evowa6k8lc">
        <ImageOff className={iconCls[size]} aria-hidden="true" data-id="element-1185" data-mp="291tklrap8b3q" />
      </span>;
  }
  if (!src) {
    const Icon = kind === 'persona' ? UserRound : kind === 'item' ? Package : Skull;
    return <span className={`${frame} flex items-center justify-center text-gray-600`} aria-hidden="true" data-id="element-1186" data-mp="3oiol1yhho6q7">
        <Icon className={iconCls[size]} data-id="element-1187" data-mp="3kj67jnrzyxxe" />
      </span>;
  }
  const stop = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  if (!preview) {
    return <span className={`${frame} flex items-center justify-center`} role={sprite ? 'img' : undefined} aria-label={sprite ? alt : undefined} data-id="element-1188" data-mp="g3ws2nld1a7r">
        {visual('thumb')}
      </span>;
  }
  return <>
      <span ref={thumbRef} tabIndex={focusable ? 0 : undefined} aria-label={focusable ? `Imagen de ${label}` : undefined} onMouseEnter={() => canHover() && show()} onMouseLeave={scheduleClose} onFocus={() => canHover() && show()} onBlur={() => setOpen(false)} onClick={e => {
      stop(e);
      // En celular, tocar la foto abre su vista previa. En computadora no hace nada (la vista previa sale al pasar el cursor).
      if (!canHover()) setSheet(true);
    }} onMouseDown={e => e.preventDefault()} onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && stop(e)} className={`${frame} flex cursor-default items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D4AF37]`} data-id="element-1189" data-mp="7a9zf6cg8n2s">
        {/* La miniatura rellena todo el cuadro (sin franjas vacías); la vista previa muestra la imagen completa. */}
        {visual('thumb')}
      </span>
      {typeof document !== 'undefined' && createPortal(<AnimatePresence data-id="element-1190" data-mp="h3xy8hjgfe1x">
            {open && <motion.div ref={previewRef} role="tooltip" aria-hidden="true" onMouseEnter={cancelClose} onMouseLeave={scheduleClose} onClick={stop} initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: pos ? 1 : 0,
        scale: pos ? 1 : 0.96
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: reduceMotion ? 0 : 0.16,
        ease: [0.23, 1, 0.32, 1]
      }} style={{
        position: 'fixed',
        left: pos?.left ?? -9999,
        top: pos?.top ?? -9999,
        zIndex: 60
      }} className="max-w-[min(18rem,calc(100vw-16px))] rounded-2xl border border-white/10 bg-[#0A1628] p-2 shadow-[0_20px_60px_rgba(0,0,0,0.6)]" data-id="element-1191" data-mp="2xuek3rwxz604">
                <div className="flex items-center justify-center rounded-xl bg-[#050b14]" data-id="element-1192" data-mp="2lbai9qp0i9nw">
                  {/* Tamaño natural como máximo: no se amplían de más las imágenes pequeñas. */}
                  {visual('preview')}
                </div>
                <p className="px-1.5 pb-0.5 pt-2 text-sm font-semibold text-white" data-id="element-1193" data-mp="1stoq5uqzm1st">{label}</p>
              </motion.div>}
          </AnimatePresence>, document.body)}
      {typeof document !== 'undefined' && createPortal(<AnimatePresence data-id="element-1194" data-mp="221ezk42jppa2">
            {sheet && <motion.div role="dialog" aria-modal="true" aria-label={`Imagen de ${label}`} onClick={e => {
        stop(e);
        setSheet(false);
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: reduceMotion ? 0 : 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-5 backdrop-blur-sm" data-id="element-1195" data-mp="21zu1i4h0vo7g">
                <motion.div onClick={stop} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} transition={{
          duration: reduceMotion ? 0 : 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#0A1628] p-2 shadow-[0_20px_60px_rgba(0,0,0,0.6)]" data-id="element-1196" data-mp="19b6bjvlbwank">
                  <div className="flex items-center justify-center overflow-hidden rounded-xl bg-[#050b14]" data-id="element-1197" data-mp="z9vnip7v49j6">
                    {visual('sheet')}
                  </div>
                  <div className="flex items-center justify-between gap-3 px-1.5 pb-0.5 pt-2" data-id="element-1198" data-mp="6lgb8ag485bj">
                    <p className="min-w-0 truncate text-sm font-semibold text-white" data-id="element-1199" data-mp="biylq59s6sln">{label}</p>
                    <button type="button" onClick={e => {
              stop(e);
              setSheet(false);
            }} aria-label="Cerrar vista previa" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 text-gray-200 transition-colors duration-150 active:bg-white/10" data-id="element-1200" data-mp="giba4ugaw7qc">
                      <X className="h-5 w-5" aria-hidden="true" data-id="element-1201" data-mp="3gvly9g01xz1e" />
                    </button>
                  </div>
                </motion.div>
              </motion.div>}
          </AnimatePresence>, document.body)}
    </>;
}

/** Miniatura de un monstruo o NPC solo si tiene imagen asignada (no ensucia listados con marcadores). */
export function MonsterThumb({
  entry,
  name,
  focusable = true
}: {
  entry?: ImageEntry;
  name: string;
  focusable?: boolean;
}) {
  if (!entry?.image) return null;
  return <EntityImage entry={entry} alt={name} name={name} focusable={focusable} kind={entry.kind === 'npc' ? 'persona' : 'monstruo'} data-id="element-1202" data-mp="37xtfu2z1ab9s" />;
}